import React, { useRef } from "react";
import img from "../../../constant";
import { X, CheckSquare } from "lucide-react";

interface GoLiveContentProps {
  clientName: string;
  representativeName: string;
  representativeRole: string;
  date: string;
  handoverDate: string;
  uploadedSignature?: File | null;
  signaturePreview?: string | null;
  onFileUpload?: (file: File) => void;
  onRemoveSignature?: () => void;
  readOnly?: boolean;
}

interface CheckItemProps {
  children: React.ReactNode;
}

const CheckItem: React.FC<CheckItemProps> = ({ children }) => (
  <li className="flex items-start gap-2 py-1">
    <CheckSquare size={16} className="text-primary mt-0.5 shrink-0" />
    <span>{children}</span>
  </li>
);

const GoLiveContent: React.FC<GoLiveContentProps> = ({
  clientName,
  date,
  handoverDate,
  representativeName,
  representativeRole,
  uploadedSignature,
  signaturePreview,
  onFileUpload,
  onRemoveSignature,
  readOnly = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!validTypes.includes(file.type)) {
        alert("Please upload a valid image file (JPG, JPEG, or PNG)");
        return;
      }
      onFileUpload?.(file);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemoveSignature?.();
  };

  return (
    <div className="font-abel text-gray-700 text-sm leading-relaxed space-y-6 p-5 bg-white shadow-sm rounded-lg border border-gray-100 max-w-[1024px] mx-auto">
      {/* Header */}
      <div className="text-center mb-10 border-b pb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">25th Suite</h1>
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">
          Go Live and Handover Checklist
        </h2>
        <div className="mt-4 space-y-1 text-base">
          <p>
            <span className="font-semibold">Client:</span>{" "}
            <span className="text-primary font-bold">{clientName}</span>
          </p>
          <p>
            <span className="font-semibold">Date of Handover:</span>{" "}
            {handoverDate}
          </p>
          {/* <p className="text-gray-500 mt-2">Prepared by 25th and Staffing</p> */}
        </div>
      </div>

      {/* Purpose */}
      <section>
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
          <h3 className="font-bold text-base mb-2 text-blue-800">Purpose</h3>
          <p className="text-gray-700 text-sm leading-relaxed">
            This checklist confirms that the 25th Suite has been successfully
            integrated, tested, trained, and handed over to the client team.
            Both parties should review and sign off on each item.
          </p>
        </div>
      </section>

      {/* Section 1 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          Section 1: System Access and Credentials
        </h3>
        <ul className="space-y-1 pl-1">
          <CheckItem>
            All user accounts created and roles assigned for HR, Managers,
            Admins, and Employees
          </CheckItem>
          <CheckItem>
            Login credentials securely shared via encrypted email or password
            manager
          </CheckItem>
          <CheckItem>
            Admin access granted to designated client IT and HR team members
          </CheckItem>
          {/* <CheckItem>
            Custom domain or white label branding verified and live where
            applicable
          </CheckItem>
          <CheckItem>
            Mobile app access tested and distributed where licensed
          </CheckItem> */}
        </ul>
      </section>

      {/* Section 2 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          Section 2: Integration and Data Confirmation
        </h3>
        <ul className="space-y-1 pl-1">
          <CheckItem>All integrations completed</CheckItem>
          <CheckItem>
            Test data migrated or loaded and validated such as sample candidates
          </CheckItem>
          <CheckItem>
            Live data synchronization confirmed with no data loss or duplication
          </CheckItem>
          <CheckItem>
            Custom rules and workflows configured and tested where applicable
          </CheckItem>
          <CheckItem>
            Notification engine tested and email or SMS templates branded and
            working
          </CheckItem>
        </ul>
      </section>

      {/* Section 3 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          Section 3: Training and Knowledge Transfer
        </h3>
        <ul className="space-y-1 pl-1">
          <CheckItem>Training sessions completed for up to 10 users</CheckItem>
          <CheckItem>
            Admin, IT and HR training completed covering system navigation,
            workflows, and reporting
          </CheckItem>
          <CheckItem>
            Manager training completed covering performance reviews, leave
            approvals, and disciplinary processes
          </CheckItem>
          {/* <CheckItem>
            Employee self service training completed covering profile updates
            and leave requests
          </CheckItem> */}
          <CheckItem>
            Training recordings, slides, or handouts shared with client
          </CheckItem>
          <CheckItem>
            Knowledge base or help documentation access provided
          </CheckItem>
          <CheckItem>Dedicated account manager</CheckItem>
        </ul>
      </section>

      {/* Section 4 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          Section 4: Documentation and Deliverables
        </h3>
        <ul className="space-y-1 pl-1">
          <CheckItem>
            Application documentation delivered including user guides, training
            guide where applicable
          </CheckItem>
          <CheckItem>
            Configuration summary shared including custom rules, integrations,
            and branding settings
          </CheckItem>
          <CheckItem>Data export and backup procedures explained</CheckItem>
          <CheckItem>
            Support contact details provided including account manager,
            escalation matrix, and ticketing portal
          </CheckItem>
          {/* <CheckItem>
            Invoice for final 30 percent balance sent and acknowledged where
            milestone based
          </CheckItem> */}
        </ul>
      </section>

      {/* Section 5 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          Section 5: Go Live Confirmation
        </h3>
        <ul className="space-y-1 pl-1">
          <CheckItem>
            System fully live and stable with no critical issues
          </CheckItem>
          <CheckItem>Pilot phase completed where applicable</CheckItem>
          <CheckItem>
            Client team has tested key workflows from start to finish
          </CheckItem>
          <CheckItem>
            Sign off received from client project lead or executive sponsor
          </CheckItem>
        </ul>
      </section>

      {/* Sign Off */}
      <section>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
          <h3 className="font-bold text-lg mb-3 text-gray-800">Sign Off</h3>
          <p className="text-sm text-gray-700 mb-4 leading-relaxed">
            We confirm that the 25th Suite has been successfully integrated,
            tested, trained, and handed over as per the Master Services
            Agreement and agreed scope.
          </p>
        </div>
      </section>

      {/* Next Steps */}
      <section>
        <h3 className="font-bold text-base mb-2 text-gray-900">Next Steps</h3>
        <ul className="space-y-1 pl-1">
          <CheckItem>Ongoing monthly support begins</CheckItem>
        </ul>
      </section>

      {/* Signatures */}
      <div className="mt-10 border-t pt-8">
        <h3 className="font-bold text-xl mb-6">Signatures</h3>
        <p className="text-sm text-gray-600 mb-6">
          IN WITNESS WHEREOF, the parties have caused this handover checklist to
          be executed by their duly authorized representatives as of the date
          indicated.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {/* 25th & Staffing side */}
          <div className="border p-6 rounded-lg bg-gray-50">
            <p className="font-bold mb-4">For: 25th and Staffing</p>
            <div className="space-y-3">
              <img src={img.atsuSign} alt="Atsu Sign" className="h-16" />
              <p className="border-b border-gray-400 pb-1">
                <strong>Name:</strong> Atsu Emmanuel
              </p>
              <p className="border-b border-gray-400 pb-1">
                <strong>Title:</strong> CTO
              </p>
              <p className="border-b border-gray-400 pb-1">
                <strong>Date:</strong> {date}
              </p>
            </div>
          </div>

          {/* Client side */}
          <div className="border p-6 rounded-lg bg-gray-50">
            <p className="font-bold mb-4">For: {clientName}</p>
            <div className="space-y-3">
              <div className="h-16 w-full">
                {readOnly ? (
                  <div className="h-16 flex items-end">
                    <span className="border-b border-gray-400 block w-48 text-gray-400 text-xs pb-1">
                      Signature
                    </span>
                  </div>
                ) : !signaturePreview ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload Signature
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleFileChange}
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-md file:border-0
                        file:text-sm file:font-semibold
                        file:bg-primary file:text-white
                        hover:file:bg-primary/90
                        cursor-pointer"
                    />
                  </div>
                ) : (
                  <div
                    className="relative group cursor-pointer"
                    onClick={handleImageClick}
                  >
                    <img
                      src={signaturePreview}
                      alt="Uploaded Signature"
                      className="h-16 object-contain"
                    />
                    <button
                      onClick={handleRemoveClick}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1
                        opacity-0 group-hover:opacity-100 transition-opacity duration-200
                        hover:bg-red-600"
                      aria-label="Remove signature"
                    >
                      <X size={16} />
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </div>
                )}
              </div>
              <p className="border-b border-gray-400 pb-1">
                <strong>Name: </strong>
                {representativeName}
              </p>
              <p className="border-b border-gray-400 pb-1">
                <strong>Title: </strong>
                {representativeRole}
              </p>
              <p className="border-b border-gray-400 pb-1">
                <strong>Date:</strong> {date}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoLiveContent;
